import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { 
  Search, Filter, Star, MapPin, Music, Calendar,
  ChevronDown, X, SlidersHorizontal
} from 'lucide-react';
import { ArtistBadges } from '../components/VerificationBadges';
import { toast } from 'sonner';

const GENRES = [
  'Riddim', 'Dubstep', 'DeepDub', 'Experimental',
  'House', 'Techno', 'Trance', 'Drum & Bass', 'EDM',
  'Hip Hop', 'R&B', 'Pop', 'Rock', 'Jazz', 'Reggae', 'Latin',
  'Afrobeats', 'Amapiano', 'Bass House', 'Future Bass',
  'Progressive House', 'Deep House', 'Tech House', 'Melodic Techno'
];

const SORT_OPTIONS = [
  { value: 'rating', label: 'Highest Rated' },
  { value: 'reviews', label: 'Most Reviews' },
  { value: 'newest', label: 'Newest' }
];

export default function ArtistSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalArtists, setTotalArtists] = useState(0);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedGenres, setSelectedGenres] = useState(
    searchParams.get('genres')?.split(',').filter(Boolean) || []
  );
  const [minRating, setMinRating] = useState(searchParams.get('min_rating') || '');
  const [location, setLocation] = useState(searchParams.get('location') || '');
  const [availableDate, setAvailableDate] = useState(searchParams.get('available_date') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort_by') || 'rating');
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);

  const fetchArtists = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (selectedGenres.length) params.append('genres', selectedGenres.join(','));
      if (minRating) params.append('min_rating', minRating);
      if (location) params.append('location', location);
      if (availableDate) params.append('available_date', availableDate);
      params.append('sort_by', sortBy);
      params.append('page', page.toString());
      
      const response = await apiClient.get(`/artists/search?${params.toString()}`);
      setArtists(response.data.artists);
      setTotalPages(response.data.pages);
      setTotalArtists(response.data.total);
      
      // Update URL
      setSearchParams(params);
    } catch (error) {
      toast.error('Failed to search artists');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedGenres, minRating, location, availableDate, sortBy, page, setSearchParams]);

  useEffect(() => {
    fetchArtists();
  }, [fetchArtists]);

  const toggleGenre = (genre) => {
    setSelectedGenres(prev => 
      prev.includes(genre) 
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
    setPage(1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedGenres([]);
    setMinRating('');
    setLocation('');
    setAvailableDate('');
    setSortBy('rating');
    setPage(1);
  };

  const hasActiveFilters = searchQuery || selectedGenres.length || minRating || location || availableDate;

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Find Artists</h1>
          <p className="text-white/60">Search and filter to find the perfect artist for your event</p>
        </div>

        {/* Search Bar */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <Input
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              placeholder="Search by name, genre, style..."
              className="pl-12 bg-white/5 border-white/10 text-white h-12"
            />
          </div>
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant={showFilters ? "default" : "outline"}
            className={showFilters ? "bg-violet-600" : ""}
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filters
            {hasActiveFilters && (
              <span className="ml-2 w-5 h-5 rounded-full bg-violet-500 text-xs flex items-center justify-center">
                !
              </span>
            )}
          </Button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card className="bg-white/5 border-white/10 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Filters</h3>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="w-4 h-4 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* Min Rating */}
                <div>
                  <Label className="text-white/80 text-sm">Minimum Rating</Label>
                  <select
                    value={minRating}
                    onChange={(e) => { setMinRating(e.target.value); setPage(1); }}
                    className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="">Any Rating</option>
                    <option value="4.5">4.5+ Stars</option>
                    <option value="4">4+ Stars</option>
                    <option value="3.5">3.5+ Stars</option>
                    <option value="3">3+ Stars</option>
                  </select>
                </div>

                {/* Location */}
                <div>
                  <Label className="text-white/80 text-sm">Location</Label>
                  <Input
                    value={location}
                    onChange={(e) => { setLocation(e.target.value); setPage(1); }}
                    placeholder="City or area"
                    className="mt-1 bg-white/5 border-white/10 text-white"
                  />
                </div>

                {/* Available Date */}
                <div>
                  <Label className="text-white/80 text-sm">Available On</Label>
                  <Input
                    type="date"
                    value={availableDate}
                    onChange={(e) => { setAvailableDate(e.target.value); setPage(1); }}
                    className="mt-1 bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>

              {/* Genres */}
              <div>
                <Label className="text-white/80 text-sm mb-2 block">Genres</Label>
                <div className="flex flex-wrap gap-2">
                  {GENRES.map((genre) => (
                    <button
                      key={genre}
                      onClick={() => toggleGenre(genre)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        selectedGenres.includes(genre)
                          ? 'bg-violet-600 text-white'
                          : 'bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sort & Results Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-white/60">
            {totalArtists} artist{totalArtists !== 1 ? 's' : ''} found
          </p>
          <div className="flex items-center gap-2">
            <span className="text-white/60 text-sm">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
          </div>
        ) : artists.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-12 text-center">
              <Music className="w-16 h-16 mx-auto mb-4 text-white/40" />
              <h3 className="text-xl font-semibold text-white mb-2">No artists found</h3>
              <p className="text-white/60 mb-4">Try adjusting your filters or search terms</p>
              <Button onClick={clearFilters} variant="outline">Clear Filters</Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {artists.map((artist) => (
                <Card 
                  key={artist.artist_id} 
                  className="bg-white/5 border-white/10 overflow-hidden cursor-pointer hover:border-violet-500/50 transition-colors"
                  onClick={() => navigate(`/artist/${artist.artist_id}`)}
                >
                  <div className="aspect-video bg-gradient-to-br from-violet-900/50 to-purple-900/50 relative">
                    {artist.profile_image ? (
                      <img src={artist.profile_image} alt={artist.bio} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music className="w-16 h-16 text-white/20" />
                      </div>
                    )}
                    {artist.rating && (
                      <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <span className="text-white text-sm font-medium">{artist.rating}</span>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-white line-clamp-1">{artist.bio?.slice(0, 50) || 'Artist'}</h3>
                      <ArtistBadges artist={artist} size="xs" />
                    </div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {artist.genres?.slice(0, 3).map((genre, i) => (
                        <span key={i} className="px-2 py-0.5 bg-violet-500/20 text-violet-300 rounded-full text-xs">
                          {genre}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1 text-white/60">
                        <MapPin className="w-4 h-4" />
                        {artist.local_area || 'Available'}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 text-white/60">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
